import { useEffect, useState, useCallback } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import ClientDialog from "@/components/admin/ClientDialog";
import ItineraryEditor from "@/components/admin/ItineraryEditor";
import { formatMoney, formatShortDate } from "@/lib/helpers";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Users,
  Plane,
  CalendarCheck,
  Wallet,
  Plus,
  Pencil,
  Trash2,
  LogOut,
  MapPin,
  Loader2,
} from "lucide-react";

const Stat = ({ icon: Icon, label, value, testid }) => (
  <motion.div
    initial={{ opacity: 0, y: 12 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.5 }}
    className="glass rounded-xl p-6"
    data-testid={testid}
  >
    <div className="flex items-center justify-between mb-4">
      <span className="text-xs tracking-[0.2em] uppercase text-muted-foreground">{label}</span>
      <Icon className="w-4 h-4 text-primary" />
    </div>
    <p className="font-serif text-4xl font-light tracking-tighter text-white">{value}</p>
  </motion.div>
);

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const [stats, setStats] = useState(null);
  const [clients, setClients] = useState([]);
  const [itineraries, setItineraries] = useState([]);
  const [loading, setLoading] = useState(true);

  const [clientDialog, setClientDialog] = useState({ open: false, client: null });
  const [itinDialog, setItinDialog] = useState({ open: false, itinerary: null, defaultClientId: "" });
  const [confirm, setConfirm] = useState(null); // {type, id, label}

  const load = useCallback(async () => {
    try {
      const [s, c, i] = await Promise.all([
        api.get("/admin/stats"),
        api.get("/admin/clients"),
        api.get("/admin/itineraries"),
      ]);
      setStats(s.data);
      setClients(c.data);
      setItineraries(i.data);
    } catch (e) {
      toast.error("Failed to load dashboard");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const doDelete = async () => {
    if (!confirm) return;
    try {
      if (confirm.type === "client") await api.delete(`/admin/clients/${confirm.id}`);
      else await api.delete(`/admin/itineraries/${confirm.id}`);
      toast.success("Deleted");
      setConfirm(null);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="w-6 h-6 text-primary animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background relative z-10 pb-24">
      {/* Nav */}
      <nav className="glass sticky top-0 z-40">
        <div className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="/logo.png" alt="Travel Mor" className="w-10 h-10 object-contain" />
            <div>
              <span className="font-serif text-xl tracking-tighter block leading-none">Travel Mor</span>
              <span className="text-[10px] tracking-[0.3em] uppercase text-primary/70">Admin Studio</span>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <span className="text-sm text-muted-foreground hidden sm:block">{user?.email}</span>
            <Button data-testid="admin-logout-btn" variant="ghost" onClick={logout} className="text-muted-foreground hover:text-foreground">
              <LogOut className="w-4 h-4 sm:mr-2" />
              <span className="hidden sm:inline">Sign out</span>
            </Button>
          </div>
        </div>
      </nav>

      <div className="max-w-6xl mx-auto px-6 pt-10">
        <h1 className="font-serif text-4xl sm:text-5xl font-light tracking-tighter mb-1">Dashboard</h1>
        <p className="text-muted-foreground mb-10">Curate journeys and manage your clients.</p>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-12">
          <Stat icon={Users} label="Total Clients" value={stats?.total_clients ?? 0} testid="stat-clients" />
          <Stat icon={Plane} label="Itineraries" value={stats?.total_itineraries ?? 0} testid="stat-itineraries" />
          <Stat icon={CalendarCheck} label="Upcoming Trips" value={stats?.upcoming_trips ?? 0} testid="stat-upcoming" />
          <Stat icon={Wallet} label="Outstanding" value={formatMoney(stats?.outstanding_balance ?? 0)} testid="stat-outstanding" />
        </div>

        <Tabs defaultValue="itineraries" className="w-full">
          <TabsList className="bg-secondary/50 border border-border mb-8">
            <TabsTrigger value="itineraries" data-testid="tab-itineraries" className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">Itineraries</TabsTrigger>
            <TabsTrigger value="clients" data-testid="tab-clients" className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">Clients</TabsTrigger>
          </TabsList>

          {/* Itineraries */}
          <TabsContent value="itineraries">
            <div className="flex justify-between items-center mb-6">
              <h2 className="font-serif text-2xl font-light tracking-tighter">All Itineraries</h2>
              <Button
                data-testid="new-itinerary-btn"
                onClick={() => {
                  if (clients.length === 0) return toast.error("Create a client first");
                  setItinDialog({ open: true, itinerary: null, defaultClientId: "" });
                }}
                className="rounded-full bg-primary text-primary-foreground hover:scale-[1.02] transition-transform gold-glow"
              >
                <Plus className="w-4 h-4 mr-1" /> New itinerary
              </Button>
            </div>

            {itineraries.length === 0 ? (
              <div className="glass rounded-xl p-12 text-center text-muted-foreground" data-testid="itineraries-empty">
                No itineraries yet. Create your first journey.
              </div>
            ) : (
              <div className="space-y-3" data-testid="itineraries-list">
                {itineraries.map((it) => (
                  <div key={it.id} className="glass rounded-xl p-5 flex flex-col sm:flex-row sm:items-center gap-4" data-testid={`itinerary-row-${it.id}`}>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-3 flex-wrap">
                        <h3 className="font-serif text-2xl font-light tracking-tighter text-white truncate">{it.title}</h3>
                        {it.destination && (
                          <span className="text-xs text-muted-foreground flex items-center gap-1">
                            <MapPin className="w-3 h-3 text-primary" /> {it.destination}
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-muted-foreground mt-1">
                        {it.client_name} · {formatShortDate(it.start_date)} · {(it.days || []).length} days
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="text-[10px] tracking-widest uppercase text-muted-foreground">Remaining</p>
                      <p className={`font-serif text-2xl font-light tracking-tighter ${Number(it.remaining_balance) <= 0 ? "text-emerald-400" : "gold-text"}`}>
                        {formatMoney(it.remaining_balance, it.currency)}
                      </p>
                    </div>
                    <div className="flex gap-2">
                      <Button data-testid={`edit-itinerary-${it.id}`} variant="ghost" size="icon" onClick={() => setItinDialog({ open: true, itinerary: it, defaultClientId: "" })} className="text-muted-foreground hover:text-primary">
                        <Pencil className="w-4 h-4" />
                      </Button>
                      <Button data-testid={`delete-itinerary-${it.id}`} variant="ghost" size="icon" onClick={() => setConfirm({ type: "itinerary", id: it.id, label: it.title })} className="text-muted-foreground hover:text-destructive-foreground">
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Clients */}
          <TabsContent value="clients">
            <div className="flex justify-between items-center mb-6">
              <h2 className="font-serif text-2xl font-light tracking-tighter">All Clients</h2>
              <Button data-testid="new-client-btn" onClick={() => setClientDialog({ open: true, client: null })} className="rounded-full bg-primary text-primary-foreground hover:scale-[1.02] transition-transform gold-glow">
                <Plus className="w-4 h-4 mr-1" /> New client
              </Button>
            </div>

            {clients.length === 0 ? (
              <div className="glass rounded-xl p-12 text-center text-muted-foreground" data-testid="clients-empty">
                No clients yet. Add your first client to get started.
              </div>
            ) : (
              <div className="glass rounded-xl overflow-hidden" data-testid="clients-list">
                {clients.map((c) => (
                  <div key={c.id} className="flex items-center gap-4 px-5 py-4 border-b border-border last:border-0" data-testid={`client-row-${c.id}`}>
                    <div className="w-10 h-10 rounded-full bg-primary/15 text-primary flex items-center justify-center font-serif text-lg">
                      {c.name?.[0]?.toUpperCase() || "?"}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-foreground truncate">{c.name}</p>
                      <p className="text-sm text-muted-foreground truncate">{c.email}{c.phone ? ` · ${c.phone}` : ""}</p>
                    </div>
                    <span className="text-sm text-muted-foreground hidden sm:block">{c.itinerary_count} trip{c.itinerary_count === 1 ? "" : "s"}</span>
                    <div className="flex gap-1">
                      <Button data-testid={`add-trip-${c.id}`} variant="ghost" size="icon" title="Add itinerary" onClick={() => setItinDialog({ open: true, itinerary: null, defaultClientId: c.id })} className="text-muted-foreground hover:text-primary">
                        <Plane className="w-4 h-4" />
                      </Button>
                      <Button data-testid={`edit-client-${c.id}`} variant="ghost" size="icon" onClick={() => setClientDialog({ open: true, client: c })} className="text-muted-foreground hover:text-primary">
                        <Pencil className="w-4 h-4" />
                      </Button>
                      <Button data-testid={`delete-client-${c.id}`} variant="ghost" size="icon" onClick={() => setConfirm({ type: "client", id: c.id, label: c.name })} className="text-muted-foreground hover:text-destructive-foreground">
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>

      <ClientDialog
        open={clientDialog.open}
        client={clientDialog.client}
        onOpenChange={(o) => setClientDialog((s) => ({ ...s, open: o }))}
        onSaved={load}
      />
      <ItineraryEditor
        open={itinDialog.open}
        itinerary={itinDialog.itinerary}
        defaultClientId={itinDialog.defaultClientId}
        clients={clients}
        onOpenChange={(o) => setItinDialog((s) => ({ ...s, open: o }))}
        onSaved={load}
      />

      <AlertDialog open={!!confirm} onOpenChange={(o) => !o && setConfirm(null)}>
        <AlertDialogContent className="bg-card border-border" data-testid="delete-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-serif text-2xl font-light tracking-tighter">Delete {confirm?.type}?</AlertDialogTitle>
            <AlertDialogDescription className="text-muted-foreground">
              "{confirm?.label}" will be permanently removed{confirm?.type === "client" ? ", along with all their itineraries" : ""}. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="border-border bg-transparent hover:bg-secondary">Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="confirm-delete-btn" onClick={doDelete} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
